import { format } from 'date-fns';
import { Ban, RotateCcw, UserRoundCheck } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type BetaAccess, betaAccessStore } from '@/stores';

interface BetaAccessTableProps {
  onRevoke: (userId: string) => void;
  onRestore: (userId: string) => void;
}

function formatDateTime(value?: string) {
  if (!value) {
    return '-';
  }
  return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
}

function getGrantSourceLabel(source: string) {
  switch (source) {
    case 'admin':
      return '手动开通';
    case 'waitlist':
      return 'Waitlist';
    case 'invite_code':
      return '邀请码';
    default:
      return source || '-';
  }
}

export const BetaAccessTable = observer(
  ({ onRevoke, onRestore }: BetaAccessTableProps) => {
    const columns: Column<BetaAccess>[] = [
      {
        id: 'userId',
        header: '用户 ID',
        accessor: (row) => <span className="font-mono text-sm">{row.userId}</span>,
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row) =>
          row.status === 'active' ? (
            <Badge variant="success">
              <UserRoundCheck className="mr-1 h-3.5 w-3.5" />
              已开通
            </Badge>
          ) : (
            <Badge variant="destructive">
              <Ban className="mr-1 h-3.5 w-3.5" />
              已撤销
            </Badge>
          ),
      },
      {
        id: 'grantSource',
        header: '资格来源',
        accessor: (row) => getGrantSourceLabel(row.grantSource),
      },
      {
        id: 'grantRefType',
        header: '来源类型',
        accessor: (row) => row.grantRefType || '-',
      },
      {
        id: 'grantRefId',
        header: '来源 ID',
        accessor: (row) => row.grantRefId || '-',
      },
      {
        id: 'grantedBy',
        header: '开通操作人',
        accessor: (row) => row.grantedBy || '-',
      },
      {
        id: 'grantedAt',
        header: '开通时间',
        accessor: (row) => formatDateTime(row.grantedAt),
      },
      {
        id: 'revokedBy',
        header: '撤销操作人',
        accessor: (row) => row.revokedBy || '-',
      },
      {
        id: 'revokedAt',
        header: '撤销时间',
        accessor: (row) => formatDateTime(row.revokedAt),
      },
      {
        id: 'updatedAt',
        header: '更新时间',
        accessor: (row) => formatDateTime(row.updatedAt),
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row) => (
          row.status === 'active' ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={betaAccessStore.loading}
              onClick={() => onRevoke(row.userId)}
            >
              <Ban className="mr-1 h-4 w-4" />
              撤销资格
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={betaAccessStore.loading}
              onClick={() => onRestore(row.userId)}
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              加回去
            </Button>
          )
        ),
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={betaAccessStore.betaAccesses}
        loading={betaAccessStore.loading}
        emptyMessage="暂无内测用户"
      />
    );
  },
);
